import Header from "../component/menu/Header";
import { constants } from "../constant/constants";
import SideMenu from "../component/menu/SideMenu";
import { serverImgFullPath } from "../utils/Common";
import { Button, message, Modal, Select } from "antd";
import CustomLoader from "../component/menu/CustomLoader";
import { ConfigProvider, Divider, Spin } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import QRCode from "qrcode";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import QRCodeGeneratorOptionComponent from "../component/QRCode/QRCodeGeneratorOptionComponent";
import jsPDF from "jspdf";
import useScrollLock from "../hooks/useScrollLock";

function RestaurantQr() {
  const location = useLocation();
  const [qrData, setQrData] = useState(location.state?.qrData || {});
  const [componentKey, setComponentKey] = useState(
    location.state?.key || new Date().getTime()
  );

  const [qrValues, setQrValues] = useState([]);
  const [qrImageUrls, setQrImageUrls] = useState([]);
  const [isImageGenerating, setIsImageGenerating] = useState(false);
  const qrCanvasRefs = useRef([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedDownloadOption, setSelectedDownloadOption] =
    useState("QR Stand");
  const [currentDownloadIndex, setCurrentDownloadIndex] = useState(null);

  const [isTableWiseQR, setIsTableWiseQR] = useState(false);
  const [numberOfTable, setNumberOfTable] = useState(1);
  const [generateButtonClicked, setGenerateButtonClicked] = useState(false);

  // Lock scroll when modal is open
  useScrollLock(isModalVisible);

  useEffect(() => {
    if (location.state?.qrData) {
      setQrData({
        ...location.state.qrData,
      });
      setComponentKey(location.state.key || new Date().getTime());
    }
  }, [location.state]);

  useEffect(() => {
    if (qrData?.slug) {
      const segment = qrData.isRestaurant ? "rs" : "gr";
      const baseUrl = `${process.env.REACT_APP_FRONTEND_BASE_URL}/${segment}/${qrData.slug}`;

      if (isTableWiseQR && numberOfTable > 0) {
        const values = Array.from({ length: numberOfTable }).map(
          (_, index) => `${baseUrl}?tn=${index + 1}`
        );

        setQrValues(values);
        setQrImageUrls(Array(values.length).fill(""));
      } else {
        setQrValues([`${baseUrl}`]);
        setQrImageUrls([""]);
      }
    }
  }, [qrData, isTableWiseQR, numberOfTable]);

  useEffect(() => {
    if (generateButtonClicked) {
      qrValues.forEach((_, index) => generateQrImage(index));
    }
  }, [generateButtonClicked, qrValues]);

  const drawQrBorders = (
    ctx,
    qrX,
    qrY,
    qrSize,
    borderSize,
    strokeStyle,
    lineWidth
  ) => {
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;

    const corners = [
      {
        moveTo: [qrX + borderSize, qrY - borderSize],
        lineTo1: [qrX - borderSize, qrY - borderSize],
        lineTo2: [qrX - borderSize, qrY + borderSize],
      },
      {
        moveTo: [qrX + qrSize - borderSize, qrY - borderSize],
        lineTo1: [qrX + qrSize + borderSize, qrY - borderSize],
        lineTo2: [qrX + qrSize + borderSize, qrY + borderSize],
      },
      {
        moveTo: [qrX + borderSize, qrY + qrSize + borderSize],
        lineTo1: [qrX - borderSize, qrY + qrSize + borderSize],
        lineTo2: [qrX - borderSize, qrY + qrSize - borderSize],
      },
      {
        moveTo: [qrX + qrSize - borderSize, qrY + qrSize + borderSize],
        lineTo1: [qrX + qrSize + borderSize, qrY + qrSize + borderSize],
        lineTo2: [qrX + qrSize + borderSize, qrY + qrSize - borderSize],
      },
    ];

    corners.forEach((corner) => {
      ctx.beginPath();
      ctx.moveTo(...corner.moveTo);
      ctx.lineTo(...corner.lineTo1);
      ctx.lineTo(...corner.lineTo2);
      ctx.stroke();
    });
  };

  const loadImage = (src, crossOrigin = "Anonymous") => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = crossOrigin;
      img.onload = () => resolve(img);
      img.onerror = () => {
        console.warn(`Image failed to load: ${src}, using fallback.`);
        img.src = "/images/logo.png";
        img.onload = () => resolve(img);
      };
      img.src = src;
    });
  };

  const drawBackground = async (ctx, canvasSizeWidth, canvasSizeHeight) => {
    if (numberOfTable <= 0) {
      const bgImage = await loadImage(
        `${process.env.PUBLIC_URL}/images/bg-qr-code.jpg`
      );
      ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight);
    } else {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvasSizeWidth, canvasSizeHeight);
    }
  };

  const drawLogoAndText = async (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrX,
    qrY,
    qrSize
  ) => {
    if (numberOfTable <= 0) {
      const logoImage = await loadImage(
        serverImgFullPath(qrData?.logo, "/images/logo.png")
      );
      const name = qrData?.name || "Default Name";
      ctx.font = "italic lighter 24px Georgia";
      ctx.fillStyle = "#FFFFFF";

      const maxWidth = 200;
      let lines = [];
      let currentLine = "";

      const words = name.split(" ");
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = ctx.measureText(testLine).width;

        if (testWidth <= maxWidth) {
          currentLine = testLine;
        } else {
          lines.push(currentLine);
          currentLine = word;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      const totalTextHeight = lines.length * 40;
      const gapBetweenTextAndQr = 25;
      const gapBetweenLogoAndText = 25;
      let textYPosition = qrY - totalTextHeight - gapBetweenTextAndQr;

      const logoSize = 60;
      const logoX = qrX + (qrSize - logoSize) / 2;
      const logoY = textYPosition - logoSize - gapBetweenLogoAndText;

      ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
      textYPosition += gapBetweenLogoAndText;

      lines.forEach((line) => {
        const lineWidth = ctx.measureText(line).width;
        const lineX = qrX + (qrSize - lineWidth) / 2;
        ctx.fillText(line, lineX, textYPosition);
        textYPosition += 40;
      });
    }
  };

  const drawQrCode = async (ctx, qrCanvas, qrX, qrY, qrSize) => {
    const qrDataUrl = qrCanvas.toDataURL();
    const qrImage = await loadImage(qrDataUrl, null);
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
  };

  const drawTableText = (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrX,
    qrY,
    qrSize,
    index
  ) => {
    let text = "";
    if (isTableWiseQR && numberOfTable > 0) {
      text = `Table ${String(index + 1).padStart(2, "0")}`;
    }

    ctx.font = "italic lighter 16px Georgia";
    ctx.fillStyle = numberOfTable > 0 ? "#000000" : "#FFFFFF";
    const textWidth = ctx.measureText(text).width;
    const textX = numberOfTable > 0 ? (canvasSizeWidth - textWidth) / 2 : 177;
    const textY = numberOfTable > 0 ? 242 : 432;
    ctx.fillText(text, textX, textY);
  };

  const generateQrImage = async (index = 0) => {
    if (!qrCanvasRefs.current[index] || isImageGenerating) return;

    try {
      if (index == 0) setIsImageGenerating(true);

      const qrCanvas = qrCanvasRefs.current[index];
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const isMultipleTables = true;

      const borderSize = 10;
      let canvasSizeWidth = 512;
      let canvasSizeHeight = 724;
      let qrSize = 200;
      let qrX = (canvasSizeWidth - qrSize) / 3;
      let qrYFromBottom = (canvasSizeWidth - qrSize) / 3;
      let qrY = canvasSizeHeight - qrYFromBottom - qrSize;
      let strokeStyle = "#FFD700";
      let lineWidth = 5;

      if (isMultipleTables) {
        canvasSizeWidth = 300;
        canvasSizeHeight = 300;
        qrSize = 150;
        qrX = (canvasSizeWidth - qrSize) / 2;
        qrYFromBottom = (canvasSizeHeight - qrSize) / 2;
        qrY = (canvasSizeHeight - qrSize) / 2;
        strokeStyle = "#000000";
        lineWidth = 3;
      }

      canvas.width = canvasSizeWidth;
      canvas.height = canvasSizeHeight;

      await new Promise((resolve) => setTimeout(resolve, 100));

      await drawBackground(ctx, canvasSizeWidth, canvasSizeHeight);

      await drawLogoAndText(
        ctx,
        canvasSizeWidth,
        canvasSizeHeight,
        qrX,
        qrY,
        qrSize
      );

      await drawQrCode(ctx, qrCanvas, qrX, qrY, qrSize);

      drawQrBorders(ctx, qrX, qrY, qrSize, borderSize, strokeStyle, lineWidth);

      drawTableText(
        ctx,
        canvasSizeWidth,
        canvasSizeHeight,
        qrX,
        qrY,
        qrSize,
        index
      );

      const finalDataUrl = canvas.toDataURL("image/webp");

      setQrImageUrls((prevUrls) => {
        const updatedUrls = [...prevUrls];
        updatedUrls[index] = finalDataUrl;
        return updatedUrls;
      });
    } catch (error) {
      console.error("Error generating QR image:", error);
    } finally {
      if (index == numberOfTable - 1) setIsImageGenerating(false);
    }
  };

  const handleDownload = async (index) => {
    if (!isTableWiseQR) {
      // console.log(index, 'RestaurantQr.js', '305');
      // const link = document.createElement("a");
      // link.href = qrImageUrls[index];
      // link.download = `${qrData?.slug || "qr-code"}_${"default"}.webp`;
      // link.click();

      const showTableNumber = false
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 407;
      canvas.height = 650;

      await drawQrStand(ctx, 407, 650, qrValues[index], qrData, index, true, false, false, showTableNumber);

      const finalDataUrl = canvas.toDataURL("image/webp");
      downloadImage(
        finalDataUrl,
        `${qrData?.slug || "qr-code"}_${"default"}.webp`
      );
      
    } else {
      // console.log(index, 'RestaurantQr.js', '311');
      setCurrentDownloadIndex(index);
      setIsModalVisible(true);
    }
  };

  const downloadImage = (url, filename) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  };

  const createCanvasWithBackground = async (
    canvasSizeWidth,
    canvasSizeHeight
  ) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = canvasSizeWidth;
    canvas.height = canvasSizeHeight;

    const bgImage = await loadImage(
      `${process.env.PUBLIC_URL}/images/bg-qr-code.jpg`
    );
    ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight);

    return { canvas, ctx };
  };

  const drawQrCard = async (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrValue,
    qrData,
    selectedDownloadOption,
    index
  ) => {
    const logoImage = await loadImage(
      serverImgFullPath(qrData?.logo, "/images/logo.png")
    );
    const name = qrData?.name || "Default Name";
    ctx.font = "italic lighter 24px Georgia";
    ctx.fillStyle = "#FFFFFF";

    const maxWidth = 200;
    let lines = [];
    let currentLine = "";

    const words = name.split(" ");
    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(testLine).width;

      if (testWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    const totalTextHeight = lines.length * 40;
    const gapBetweenTextAndQr = 25;
    const gapBetweenLogoAndText = 25;
    let textYPosition =
      canvasSizeHeight - 310 - 200 - totalTextHeight - gapBetweenTextAndQr;

    const logoSize = 60;
    const logoX = 100 + (200 - logoSize) / 2;
    const logoY = textYPosition - logoSize - gapBetweenLogoAndText;

    ctx.drawImage(logoImage, logoX, logoY, logoSize, logoSize);
    textYPosition += gapBetweenLogoAndText;

    lines.forEach((line) => {
      const lineWidth = ctx.measureText(line).width;
      const lineX = (canvasSizeWidth - 200) / 3 + (200 - lineWidth) / 2;
      ctx.fillText(line, lineX, textYPosition);
      textYPosition += 40;
    });

    if (selectedDownloadOption === "QR Card") {
      const qrCanvas = document.createElement("canvas");
      qrCanvas.width = 200;
      qrCanvas.height = 200;

      await QRCode.toCanvas(qrCanvas, qrValue, {
        width: 200,
        errorCorrectionLevel: "H",
        color: { dark: "#FFFFFF", light: "#0000" },
      });

      ctx.drawImage(qrCanvas, 84, 198, 240, 230);

      drawQrBorders(
        ctx,
        (canvasSizeWidth - 200) / 3,
        canvasSizeHeight - 310 - 200,
        200,
        10,
        "#FFD700",
        5
      );

      ctx.font = "italic lighter 16px Georgia";
      const text = `Table ${String(index + 1).padStart(2, "0")}`;
      ctx.fillText(text, 170, 432);
    } else {
      const text = `Table 01`;
      ctx.fillText(text, 177, 432);
    }
  };

  const drawQrStand = async (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrValue,
    qrData,
    index,
    isSingleDownload = false,
    isSingleQRPrint = false,
    minimizeGap = false,
    showTableNumber = true
  ) => {
    const adjustedHeight = isSingleDownload ? canvasSizeHeight : canvasSizeHeight - 80;

    const bgImage = await loadImage(`${process.env.PUBLIC_URL}/images/sample-stand-design.png`);
    
    isSingleDownload ? ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight) : ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight - 80);

    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;
    ctx.setLineDash([1, 1]);
    ctx.strokeRect(0, 0, canvasSizeWidth, adjustedHeight);
    ctx.setLineDash([]);

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, adjustedHeight, canvasSizeWidth, 35);

    const name = qrData?.name || "Default Name";
    ctx.font = "normal normal normal 40px Poppins";
    ctx.fillStyle = "#000000";

    const maxWidth = 200;
    let lines = [];
    let currentLine = "";

    const words = name.split(" ");
    words.forEach((word) => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = ctx.measureText(testLine).width;

      if (testWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, canvasSizeHeight, canvasSizeWidth, 35);

    lines.forEach((line, i) => {
      const lineWidth = ctx.measureText(line).width;
      const lineX = (canvasSizeWidth - lineWidth) / 2;
      ctx.fillText(line, lineX, 75 + i * 38);
    });

    const qrCanvas = document.createElement("canvas");
    qrCanvas.width = 140;
    qrCanvas.height = 140;

    await QRCode.toCanvas(qrCanvas, qrValue, {
      width: 140,
      errorCorrectionLevel: "H",
      margin: 2,
      color: { dark: "#000000", light: "#FFFFFF" },
    });

    const qrX = (canvasSizeWidth - 140) / 2;
    let qrY;

    if (isSingleDownload) {
      if (minimizeGap) {
        qrY = (canvasSizeHeight - 360) / 2 - 10;
      } else {
        qrY = (canvasSizeHeight - 270) / 2 + 5;
      }
    } else {
      if (minimizeGap) {
        qrY = (canvasSizeHeight - 290) / 2 - 10;
      } else {
        qrY = (canvasSizeHeight - 35 - 270) / 2 + 5;
      }
    }

    ctx.drawImage(qrCanvas, qrX, qrY, 140, 140);

    const borderSize = 20;
    const strokeStyle = "#FFD700";
    const lineWidth = 5;

    drawQrBorders(ctx, qrX, qrY, 140, borderSize, strokeStyle, lineWidth);

    if (showTableNumber) {
      ctx.font = "normal normal normal 32px Poppin";
      const text = `Table #${String(index + 1).padStart(2, "0")}`;
      const textWidth = ctx.measureText(text).width;
      const textX = (canvasSizeWidth - textWidth) / 2;
      let textY = isSingleDownload ? 470 : 400;
      if (isSingleQRPrint) textY = 430;
      ctx.fillText(text, textX, textY);
    }
  };
  
  const drawQrStandTableOnly = async (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrValue,
    qrData,
    index,
    isSingleDownload = false,
    isSingleQRPrint = false,
    minimizeGap = false
  ) => {
    const adjustedHeight = isSingleDownload ? canvasSizeHeight : canvasSizeHeight - 80;

    const bgImage = await loadImage(`${process.env.PUBLIC_URL}/images/sample-stand-design.png`);
    
    isSingleDownload ? ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight) : ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight - 80);

    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;
    ctx.setLineDash([1, 1]);
    ctx.strokeRect(0, 0, canvasSizeWidth, adjustedHeight);
    ctx.setLineDash([]);

    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, adjustedHeight, canvasSizeWidth, 35);

    const qrCanvas = document.createElement("canvas");
    qrCanvas.width = 150;
    qrCanvas.height = 150;

    await QRCode.toCanvas(qrCanvas, qrValue, {
      width: 150,
      errorCorrectionLevel: "H",
      margin: 2,
      color: { dark: "#000000", light: "#FFFFFF" },
    });

    const qrX = (canvasSizeWidth - 150) / 2;
    let qrY;

    if (isSingleDownload) {
      if (minimizeGap) {
        qrY = (canvasSizeHeight - 360) / 2 - 15;
      } else {
        qrY = (canvasSizeHeight - 500) / 2 + 15;
      }
    } else {
      if (minimizeGap) {
        qrY = (canvasSizeHeight - 500) / 2 - 5;
      } else {
        qrY = (canvasSizeHeight - 500) / 2 + 5;
      }
    }

    ctx.drawImage(qrCanvas, qrX, qrY, 140, 140);

    const borderSize = 30;
    const strokeStyle = "#FFD700";
    const lineWidth = 5;

    drawQrBorders(ctx, qrX, qrY, 140, borderSize, strokeStyle, lineWidth);

    ctx.font = "normal normal bold 210px Poppin";
    const text = `${String(index + 1).padStart(2, "0")}`;
    const textWidth = ctx.measureText(text).width;
    const textX = (canvasSizeWidth - textWidth) / 2;
    let textY = isSingleDownload ? 470 : 420;
    if (isSingleQRPrint) textY = 430;
    ctx.fillStyle = "#FFD700";
    ctx.fillText(text, textX, textY);
  };

  const drawQrStandNumberOnly = async (
    ctx,
    canvasSizeWidth,
    canvasSizeHeight,
    qrValue,
    qrData,
    index,
    isSingleDownload = false,
    isSingleQRPrint = false,
    minimizeGap = false
  ) => {

    const adjustedHeight = isSingleDownload ? canvasSizeHeight : canvasSizeHeight;
    const bgImage = await loadImage(`${process.env.PUBLIC_URL}/images/design-stand-sample.png`);
    
    isSingleDownload ? ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight) : ctx.drawImage(bgImage, 0, 0, canvasSizeWidth, canvasSizeHeight);

    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 1;
    ctx.setLineDash([1, 1]);
    ctx.strokeRect(0, 0, canvasSizeWidth, adjustedHeight);
    ctx.setLineDash([]);

    const qrCanvas = document.createElement("canvas");
    qrCanvas.width = 140;
    qrCanvas.height = 140;

    await QRCode.toCanvas(qrCanvas, qrValue, {
        width: 140,
        errorCorrectionLevel: "H",
        margin: 2,
        color: { dark: "#000000", light: "#FFFFFF" },
    });

    const qrSize = 130;
    const qrX = (canvasSizeWidth - qrSize) / 2;
    const qrY = (canvasSizeHeight - qrSize) / 2 - 92;

    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    const borderSize = 30;
    const strokeStyle = "#FFD700";
    const lineWidth = 5;

    drawQrBorders(ctx, qrX, qrY, qrSize, borderSize, strokeStyle, lineWidth);

    ctx.font = "normal normal bold 210px Poppin";
    const text = `${String(index + 1).padStart(2, "0")}`;
    const textWidth = ctx.measureText(text).width;
    const textX = (canvasSizeWidth - textWidth) / 2;
    const textY = qrY + qrSize + 200;
    ctx.fillStyle = "#FFD700";
    ctx.fillText(text, textX, textY);
  };

  const handleModalDownload = async (index) => {
    if (selectedDownloadOption === "QR Code" && qrImageUrls[index]) {
      downloadImage(
        qrImageUrls[index],
        `${qrData?.slug || "qr-code"}_table_${index + 1}.webp`
      );
    } else if (selectedDownloadOption === "QR Card" && qrValues[index]) {
      const { canvas, ctx } = await createCanvasWithBackground(512, 724);
      await drawQrCard(
        ctx,
        512,
        724,
        qrValues[index],
        qrData,
        selectedDownloadOption,
        index
      );
      const finalDataUrl = canvas.toDataURL("image/webp");
      downloadImage(
        finalDataUrl,
        `${qrData?.slug || "qr-card"}_table_${index + 1}.webp`
      );
    } else if (selectedDownloadOption === "QR Stand" && qrValues[index]) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 407;
      canvas.height = 650;

      await drawQrStand(ctx, 407, 650, qrValues[index], qrData, index, true);

      const finalDataUrl = canvas.toDataURL("image/webp");
      downloadImage(
        finalDataUrl,
        `${qrData?.slug || "qr-stand"}_table_${index + 1}.webp`
      );
    } else if (selectedDownloadOption === "QR Stand Table Only" && qrValues[index]) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 407;
      canvas.height = 650;

      await drawQrStandTableOnly(ctx, 407, 650, qrValues[index], qrData, index, true);

      const finalDataUrl = canvas.toDataURL("image/webp");
      downloadImage(
        finalDataUrl,
        `${qrData?.slug || "qr-stand"}_table_only_${index + 1}.webp`
      );
    } else if (selectedDownloadOption === "QR Stand Number Only" && qrValues[index]) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 200;
      canvas.height = 400;

      await drawQrStandNumberOnly(ctx, 200, 400, qrValues[index], qrData, index, true);

      const finalDataUrl = canvas.toDataURL("image/webp");
      downloadImage(
        finalDataUrl,
        `${qrData?.slug || "qr-stand"}_number_only_${index + 1}.webp`
      );
    }
    setIsModalVisible(false);
  };

  const handleModalPrint = async () => {
    if (
      selectedDownloadOption === "QR Code" &&
      qrImageUrls[currentDownloadIndex]
    ) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Code</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img src="${
                qrImageUrls[currentDownloadIndex]
              }" alt="QR Code for ${qrData?.slug || "group"}" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    } else if (
      selectedDownloadOption === "QR Card" &&
      qrValues[currentDownloadIndex]
    ) {
      const { canvas, ctx } = await createCanvasWithBackground(512, 724);
      await drawQrCard(
        ctx,
        512,
        724,
        qrValues[currentDownloadIndex],
        qrData,
        selectedDownloadOption,
        currentDownloadIndex
      );
      const finalDataUrl = canvas.toDataURL("image/png");

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Card</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img id="qrCardImage" src="${finalDataUrl}" alt="QR Card for ${
          qrData?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        const qrCardImage = printWindow.document.getElementById("qrCardImage");
        qrCardImage.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
    } else if (
      selectedDownloadOption === "QR Stand" &&
      qrValues[currentDownloadIndex]
    ) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 407;
      canvas.height = 650;

      await drawQrStand(ctx, 407, 650, qrValues[currentDownloadIndex], qrData, currentDownloadIndex, false, true);

      const finalDataUrl = canvas.toDataURL("image/png");

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Stand</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img id="qrStandImage" src="${finalDataUrl}" alt="QR Stand for ${
          qrData?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        const qrStandImage = printWindow.document.getElementById("qrStandImage");
        qrStandImage.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
    } else if (
      selectedDownloadOption === "QR Stand Table Only" &&
      qrValues[currentDownloadIndex]
    ) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 407;
      canvas.height = 650;

      await drawQrStandTableOnly(ctx, 407, 650, qrValues[currentDownloadIndex], qrData, currentDownloadIndex, false, true);

      const finalDataUrl = canvas.toDataURL("image/png");

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Stand Table Only</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img id="qrStandImage" src="${finalDataUrl}" alt="QR Stand Table Only for ${
          qrData?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        const qrStandImage = printWindow.document.getElementById("qrStandImage");
        qrStandImage.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
    } else if (
      selectedDownloadOption === "QR Stand Number Only" &&
      qrValues[currentDownloadIndex]
    ) {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 200;
      canvas.height = 400;

      await drawQrStandNumberOnly(ctx, 200, 400, qrValues[currentDownloadIndex], qrData, currentDownloadIndex, false, true);

      const finalDataUrl = canvas.toDataURL("image/png");

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Stand Number Only</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img id="qrStandImage" src="${finalDataUrl}" alt="QR Stand Number Only for ${
          qrData?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();

        const qrStandImage = printWindow.document.getElementById("qrStandImage");
        qrStandImage.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
    }
    setIsModalVisible(false);
  };

  const handleModalDownloadAll = async () => {
    if (selectedDownloadOption === "QR Code") {
      qrImageUrls.forEach((url, index) => {
        if (url) {
          const link = document.createElement("a");
          link.href = url;
          link.download = `${qrData?.slug || "qr-code"}_table_${index + 1}.webp`;
          link.click();
        }
      });
    } else if (selectedDownloadOption === "QR Card") {
      for (let index = 0; index < qrValues.length; index++) {
        const { canvas, ctx } = await createCanvasWithBackground(512, 724);
        await drawQrCard(
          ctx,
          512,
          724,
          qrValues[index],
          qrData,
          selectedDownloadOption,
          index
        );
        const finalDataUrl = canvas.toDataURL("image/webp");
        const link = document.createElement("a");
        link.href = finalDataUrl;
        link.download = `${qrData?.slug || "qr-card"}_table_${index + 1}.webp`;
        link.click();
      }
    } else if (selectedDownloadOption === "QR Stand") {
      const pdf = new jsPDF("p", "mm", "a4");
      const qrStandWidth = 407;
      const qrStandHeight = 650;
      const padding = 10;
      const extraPadding = 10;
      const standsPerRow = 2;
      const rowsPerPage = 2;
      const topMargin = 10;
      const pageHeight = pdf.internal.pageSize.height;
      const pageWidth = pdf.internal.pageSize.width;
      const scale = Math.min(pageWidth / (standsPerRow * (qrStandWidth + padding + extraPadding) - padding), (pageHeight - topMargin) / (rowsPerPage * (qrStandHeight + padding) - padding));

      for (let index = 0; index < qrValues.length; index++) {
        const row = Math.floor(index / standsPerRow);
        const col = index % standsPerRow;
        const x = col * (qrStandWidth + padding + extraPadding) * scale + (col === 0 ? extraPadding * scale : 0);
        const y = topMargin + (row % rowsPerPage) * (qrStandHeight + padding) * scale;

        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");
        tempCanvas.width = qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0);
        tempCanvas.height = qrStandHeight;
        tempCtx.fillStyle = "#FFFFFF";
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        await drawQrStand(tempCtx, qrStandWidth, qrStandHeight, qrValues[index], qrData, index, false, false, true);
        const imgData = tempCanvas.toDataURL("image/webp");

        pdf.addImage(imgData, "PNG", x, y, (qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0)) * scale, qrStandHeight * scale);

        if ((index + 1) % (standsPerRow * rowsPerPage) === 0 && index < qrValues.length - 1) {
          pdf.addPage();
        }
      }

      pdf.save(`${qrData?.slug || "qr-stand"}_all_tables.pdf`);
    } else if (selectedDownloadOption === "QR Stand Table Only") {
      const pdf = new jsPDF("p", "mm", "a4");
      const qrStandWidth = 407;
      const qrStandHeight = 650;
      const padding = 10;
      const extraPadding = 10;
      const standsPerRow = 2;
      const rowsPerPage = 2;
      const topMargin = 10;
      const pageHeight = pdf.internal.pageSize.height;
      const pageWidth = pdf.internal.pageSize.width;
      const scale = Math.min(pageWidth / (standsPerRow * (qrStandWidth + padding + extraPadding) - padding), (pageHeight - topMargin) / (rowsPerPage * (qrStandHeight + padding) - padding));

      for (let index = 0; index < qrValues.length; index++) {
        const row = Math.floor(index / standsPerRow);
        const col = index % standsPerRow;
        const x = col * (qrStandWidth + padding + extraPadding) * scale + (col === 0 ? extraPadding * scale : 0);
        const y = topMargin + (row % rowsPerPage) * (qrStandHeight + padding) * scale;

        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");
        tempCanvas.width = qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0);
        tempCanvas.height = qrStandHeight;

        tempCtx.fillStyle = "#FFFFFF";
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        await drawQrStandTableOnly(tempCtx, qrStandWidth, qrStandHeight, qrValues[index], qrData, index, false, false, true);
        const imgData = tempCanvas.toDataURL("image/webp");

        pdf.addImage(imgData, "PNG", x, y, (qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0)) * scale, qrStandHeight * scale);

        if ((index + 1) % (standsPerRow * rowsPerPage) === 0 && index < qrValues.length - 1) {
          pdf.addPage();
        }
      }
      pdf.save(`${qrData?.slug || "qr-stand"}_table_only.pdf`);
    } else if (selectedDownloadOption === "QR Stand Number Only") {
      const pdf = new jsPDF("p", "mm", "a4");
      const qrStandWidth = 200;
      const qrStandHeight = 400;
      const padding = 10;
      const extraPadding = 42;
      const standsPerRow = 4;
      const rowsPerPage = 3;
      const topMargin = 10;
      const pageHeight = pdf.internal.pageSize.height;
      const pageWidth = pdf.internal.pageSize.width;
      const scale = Math.min(pageWidth / (standsPerRow * (qrStandWidth + padding + extraPadding) - padding), (pageHeight - topMargin) / (rowsPerPage * (qrStandHeight + padding) - padding));

      for (let index = 0; index < qrValues.length; index++) {
        const row = Math.floor(index / standsPerRow);
        const col = index % standsPerRow;
        const x = col * (qrStandWidth + padding + extraPadding) * scale + (col === 0 ? extraPadding * scale : 0);
        const y = topMargin + (row % rowsPerPage) * (qrStandHeight + padding) * scale;

        const tempCanvas = document.createElement("canvas");
        const tempCtx = tempCanvas.getContext("2d");
        tempCanvas.width = qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0);
        tempCanvas.height = qrStandHeight;

        tempCtx.fillStyle = "#FFFFFF";
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

        await drawQrStandNumberOnly(tempCtx, qrStandWidth, qrStandHeight, qrValues[index], qrData, index, false, false, true);
        const imgData = tempCanvas.toDataURL("image/webp");

        pdf.addImage(imgData, "PNG", x, y, (qrStandWidth + (col === 0 ? extraPadding : 0) + (col === standsPerRow - 1 ? extraPadding : 0)) * scale, qrStandHeight * scale);

        if ((index + 1) % (standsPerRow * rowsPerPage) === 0 && index < qrValues.length - 1) {
          pdf.addPage();
        }
      }

      pdf.save(`${qrData?.slug || "qr-stand"}_number_only.pdf`);
    }
    setIsModalVisible(false);
  };

  const handleDownloadAll = () => {
    setIsModalVisible(true);
    setCurrentDownloadIndex(null);
  };

  const handlePrint = () => {
    if (qrImageUrls) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print QR Code</title>
            </head>
            <body style="display: flex; justify-content: center; align-items: center; height: 100vh; margin: 0;">
              <img src="${qrImageUrls}" alt="QR Code for ${
          qrData?.slug || "group"
        }" />
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        qrData.onFinish?.();
      }
    }
  };

  const handleQRCodeGenerate = () => {
    if (isTableWiseQR && !numberOfTable) {
      message.error("Please enter number of table.");
      return;
    }

    if (!isTableWiseQR) {
      setNumberOfTable(1);
    }

    setGenerateButtonClicked(true);
  };

  const handleIsTableWiseQRChange = (value) => {
    setGenerateButtonClicked(false);
    setIsTableWiseQR(value);
    if (!value) {
      setNumberOfTable(1);
    }
  };

  const handleNumberOfTableChange = (value) => {
    setGenerateButtonClicked(false);
    setNumberOfTable(value);
  };

  return (
    <div key={componentKey} className="flex flex-col">
      <CustomLoader />
      <Header title={`McDonalds Food Chain`} menu={constants.MENU.FOOD} />
      <div className="flex flex-row bg-prewhite">
        <SideMenu title={`McDonalds Food Chain`} menu={constants.MENU.FOOD} />
        <div className="flex flex-row p-[24px] w-full mt-[80px] ml-[72px]">
          <div className="flex flex-col w-full">
            <div className="flex flex-row">
              <div className="flex flex-row mb-[16px] font-uregular p-[16px] w-full items-center justify-between">
                <div className="font-ubold text-xl">
                  <QRCodeGeneratorOptionComponent
                    isTableWiseQR={isTableWiseQR}
                    numberOfTable={numberOfTable}
                    handleIsTableWiseQRChange={handleIsTableWiseQRChange}
                    handleNumberOfTableChange={handleNumberOfTableChange}
                    handleQRCodeGenerate={handleQRCodeGenerate}
                  />
                </div>
              </div>
            </div>
            {
              isImageGenerating ? (
                <div className="bg-white pt-[20px] pb-[10px] flex justify-center items-center">
                  <div className="bg-white pt-[10px] pb-[10px] flex justify-center items-center" style={{ minHeight: "60px", width: "100%", overflowY: "auto" }}>
                    <Spin size="large" />
                  </div>
                </div>
              ) : generateButtonClicked ? (
                numberOfTable > 0 ? (
                  <div className="bg-white pt-[20px] pb-[10px] flex justify-center items-center">
                    <div className="bg-white pt-[10px] pb-[10px] flex justify-center items-center" style={{ minHeight: "60px", width: "100%", overflowY: "auto" }}>
                      <ConfigProvider
                        theme={{
                          components: {
                            Button: {
                              defaultHoverBorderColor: "#FFD700",
                              defaultHoverColor: "#000000",
                              fontFamily: "ubold",
                            },
                          },
                        }}
                      >
                        <div className="flex flex-col font-uregular items-center">
                          <div className="grid grid-cols-4 gap-2">
                            {Array.from({ length: numberOfTable }).map(
                              (_, index) => (
                                <div
                                  key={index}
                                  className="flex flex-col items-center"
                                >
                                  {qrImageUrls[index] ? (
                                    <>
                                      <img
                                        src={qrImageUrls[index]}
                                        alt={`QR Code ${index + 1}`}
                                      />
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/download.svg`}
                                        alt="Download"
                                        style={{
                                          cursor: "pointer",
                                          marginBottom: "20px",
                                        }}
                                        onClick={() => handleDownload(index)}
                                      />
                                    </>
                                  ) : (
                                    <div
                                      style={{ opacity: 0, position: "absolute" }}
                                    >
                                      <QRCodeCanvas
                                        key={`${qrValues}-${index}`}
                                        value={qrValues[index] || " "}
                                        size={200}
                                        level="H"
                                        ref={(el) =>
                                          (qrCanvasRefs.current[index] = el)
                                        }
                                      />
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                          <Divider />
                          <div className="flex justify-end space-x-4">
                            <Button
                              type="primary"
                              onClick={handleDownloadAll}
                              style={{
                                backgroundColor: "#FFD700",
                                color: "#000000",
                              }}
                              disabled={isImageGenerating || !qrImageUrls.length}
                            >
                              <img
                                src={`${process.env.PUBLIC_URL}/images/download-all.svg`}
                                alt="Download All"
                              />
                              Download All
                            </Button>
                          </div>
                        </div>
                      </ConfigProvider>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white pt-[20px] pb-[10px] flex justify-center items-center">
                    <div className="bg-white pt-[10px] pb-[10px] flex justify-center items-center" style={{ minHeight: "60px", width: "100%", overflowY: "auto" }}>
                      <ConfigProvider
                        theme={{
                          components: {
                            Button: {
                              defaultHoverBorderColor: "#FFD700",
                              defaultHoverColor: "#000000",
                              fontFamily: "ubold",
                            },
                          },
                        }}
                      >
                        <div className="flex flex-col font-uregular items-center">
                          <div
                            className="flex flex-row bg-white justify-center"
                            style={{ minHeight: "550px" }}
                          >
                            {qrImageUrls[0] ? (
                              <img
                                src={qrImageUrls[0]}
                                alt="QR Code"
                                style={{ width: 400, height: 550 }}
                              />
                            ) : (
                              <div style={{ opacity: 0, position: "absolute" }}>
                                <QRCodeCanvas
                                  key={`${qrValues}`}
                                  value={qrValues[0] || " "}
                                  size={250}
                                  level="H"
                                  ref={(el) => (qrCanvasRefs.current[0] = el)}
                                />
                              </div>
                            )}
                          </div>
                          <Divider />
                          <div className="flex justify-end space-x-4">
                            <Button
                              type="primary"
                              onClick={() => handleDownload(0)}
                              style={{
                                backgroundColor: "#FFD700",
                                color: "#000000",
                              }}
                              disabled={isImageGenerating || !qrImageUrls.length}
                            >
                              Download
                            </Button>
                            <Button
                              type="default"
                              onClick={handlePrint}
                              disabled={isImageGenerating || !qrImageUrls.length}
                            >
                              Print
                            </Button>
                          </div>
                        </div>
                      </ConfigProvider>
                    </div>
                  </div>
                )
              ) : (
                <></>
              )
            }
          </div>
        </div>
      </div>
      <Modal
        title="Download QR Code"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsModalVisible(false)}
            style={{
              backgroundColor: "#FFD700",
              color: "#000000",
            }}
          >
            Cancel
          </Button>,
          currentDownloadIndex !== null && (
            <Button key="print" onClick={handleModalPrint}>
              Print
            </Button>
          ),
          <Button
            key="download"
            type="primary"
            onClick={
              currentDownloadIndex === null
                ? handleModalDownloadAll
                : () => handleModalDownload(currentDownloadIndex)
            }
            style={{
              backgroundColor: "#000000",
              color: "#FFFFFF",
            }}
          >
            Download
          </Button>,
        ]}
      >
        <Select
          defaultValue="QR Stand"
          style={{ width: "100%" }}
          onChange={(value) => setSelectedDownloadOption(value)}
        >
          {/* <Select.Option value="QR Code">QR Code</Select.Option>
          <Select.Option value="QR Card">QR Card</Select.Option> */}
          <Select.Option value="QR Stand">QR Stand</Select.Option>
          <Select.Option value="QR Stand Table Only">QR Stand Table Only</Select.Option>
          <Select.Option value="QR Stand Number Only">QR Stand Number Only</Select.Option>
        </Select>
      </Modal>
    </div>
  );
}

export default RestaurantQr;
